import { ReactNode, useMemo } from 'react';
import { Collapse as CollapseBase } from 'react-collapse';

import { useToggleV2, useWatch } from 'hooks';

import { cascadeEval } from 'utils/conditions';
import { cn } from 'styles';

interface CollapseProps {
  children: ReactNode;
  expanded?: boolean;
  onlyRenderIfExpanded?: boolean;
  width?: number | string;
  className?: string;
}

// 50ms is enough to get the content height and not
// be noticeable by human eye most of the time
const DELAY_RENDER_TIME_MS = 50;

export function Collapse({
  children,
  expanded = false,
  onlyRenderIfExpanded = false,
  width,
  className,
}: CollapseProps): JSX.Element | null {
  const [delayRender, delayHandlers] = useToggleV2(expanded);

  useWatch(expanded, () => {
    setTimeout(() => delayHandlers.setIsOn(expanded), DELAY_RENDER_TIME_MS);
  });

  const style = useMemo(
    () => ({
      // necessary to ensure we don't get "phantom" growth
      // when using this inside a Column or similar
      display: cascadeEval([
        { is: !onlyRenderIfExpanded, then: 'block' },
        { is: !delayRender, then: 'none' },
        { is: true, then: 'block' },
      ]),

      transition: 'height 420ms',

      width,
    }),
    [onlyRenderIfExpanded, delayRender, width],
  );

  if (onlyRenderIfExpanded && !expanded) return null;

  return (
    <CollapseBase
      className={cn('transition-[height_420ms]', className)}
      isOpened={onlyRenderIfExpanded ? delayRender : expanded}
      style={style}
    >
      <>{children}</>
    </CollapseBase>
  );
}
